/**
 * useEquipMeasureScheduleYear
 *
 * Parallel-fetches the measure schedule for all four quarters of a given year
 * via `useQueries`. Shares the TanStack Query cache with `useMeasureSchedule`
 * (same key factory), so a component that mounts this hook and another that
 * uses the per-quarter hook will see a single in-flight request per (year, quarter).
 *
 * Returned `items` is the flattened union of all four quarters — selectors in
 * `measureScheduleSelectors.ts` filter by quarter internally.
 *
 * SYNC: Synced to equip_services/src/hooks/equip/ via update.sh
 */

import { useQueries } from '@tanstack/react-query'
import { equipApi } from '@/services/equipApi'
import { equipConfigKeys } from './useEquipConfig'
import type { MeasureScheduleItem } from '@/lib/equip-types'

export interface MeasureScheduleYear {
  items: MeasureScheduleItem[]
  isLoading: boolean
  isError: boolean
}

const QUARTERS = [1, 2, 3, 4] as const

export function useEquipMeasureScheduleYear(year: number): MeasureScheduleYear {
  const results = useQueries({
    queries: QUARTERS.map(quarter => ({
      queryKey: equipConfigKeys.measureSchedule(year, quarter),
      queryFn: () => equipApi.config.getMeasureSchedule(year, quarter),
      enabled: year > 0,
      staleTime: 10 * 60 * 1000,
    })),
  })

  const items = results.flatMap(r => r.data ?? [])
  const isLoading = results.some(r => r.isLoading)
  const isError = results.some(r => r.isError)

  return { items, isLoading, isError }
}
